import React, { useState, useRef, useMemo, useCallback } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import DepartmentModal from "../components/modal/DepartmentModal";
import API from "../api";
import toastr from "toastr";

interface Department {
  id?: number;
  dept_name: string;
  description?: string;
  status?: number;
  created?: string;
  modified?: string;
}

const Departments: React.FC = () => {
  const [showModal, setShowModal]       = useState(false);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [modalMode, setModalMode]       = useState<"add" | "edit">("add");
  const tableRef = useRef<any>(null);

  const fetchDepartments = useCallback(async (
    page: number,
    search: string,
    sortBy = "dept_name",
    sortOrder = "asc"
  ) => {
    try {
      const res = await API.get(
        `departments/getDepartments?page=${page}&pageSize=25&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      const data = res.data?.data ?? [];
      const total = res.data?.total ?? 0;
      return { data, total };
    } catch {
      toastr.error("Failed to fetch departments");
      return { data: [], total: 0 };
    }
  }, []);

  const handleAdd = () => {
    setModalMode("add");
    setSelectedDept(null);
    setShowModal(true);
  };

  const handleEdit = useCallback((dept: Department) => {
    setModalMode("edit");
    setSelectedDept(dept);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    try {
      await API.delete(`departments/deleteDepartment/${id}`);
      toastr.success("Department deleted successfully");
      tableRef.current?.refetch();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to delete department");
    }
  }, []);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDept(null);
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        id: "serial",
        cell: ({ row }: any) => (
          <span style={{ color: "#ffffff", fontSize: "13px" }}>
            {typeof row.index === "number" ? row.index + 1 : row.original?.id ?? "-"}
          </span>
        ),
      },
      {
        header: "Department Name",
        accessorKey: "dept_name",
        cell: ({ row }: any) => (
          <span style={{ color: "#ffffff", fontSize: "13px", fontWeight: 500 }}>
            {row.original.dept_name || "-"}
          </span>
        ),
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }: any) => (
          <span style={{ color: "#d1d5db", fontSize: "12px" }}>
            {row.original.description || "-"}
          </span>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }: any) => {
          const active = String(row.original.status) === "1";
          return (
            <span className={`badge ${active ? "bg-success" : "bg-secondary"}`}
              style={{ fontSize: "11px" }}>
              {active ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: Department = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-light"
                onClick={() => handleEdit(data)}
                title="Edit"
              >
                <i className="fa fa-edit" />
              </button>
              <button
                className="btn btn-light"
                onClick={() => handleDelete(data.id!)}
                title="Delete"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ContentHeader title="Departments" />
        <button className="btn btn-light" onClick={handleAdd}>
          <i className="fa fa-plus me-1" /> Create New
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchDepartments}
                queryKey={["departments"]}
              />
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <DepartmentModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          deptData={selectedDept}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default Departments;